// /app/frontend/src/components/compliance/map/NodeDetailsSidebar.jsx
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { 
  X, Database, Cpu, HardDrive, Globe, Users, Building2,
  ShieldCheck, AlertTriangle, AlertCircle, FileText, ExternalLink,
  Lock, Key, Eye, Trash2, Edit
} from 'lucide-react';
import { cn } from '../../../lib/utils';

// Node type configurations
const NODE_TYPES = {
  source: { icon: Database, color: 'text-blue-400', label: 'Data Source' },
  process: { icon: Cpu, color: 'text-purple-400', label: 'Processing System' },
  store: { icon: HardDrive, color: 'text-orange-400', label: 'Storage System' },
  transfer: { icon: Globe, color: 'text-rose-400', label: 'Cross-Border Transfer' },
  thirdparty: { icon: Building2, color: 'text-cyan-400', label: 'Third Party' },
  subjects: { icon: Users, color: 'text-emerald-400', label: 'Data Subjects' }
};

const RISK_LEVELS = {
  low: { icon: ShieldCheck, color: 'text-emerald-400 bg-emerald-500/10', label: 'Low Risk' },
  medium: { icon: AlertCircle, color: 'text-amber-400 bg-amber-500/10', label: 'Medium' },
  high: { icon: AlertTriangle, color: 'text-rose-400 bg-rose-500/10', label: 'High Risk' }
};

const NodeDetailsSidebar = ({ node, onClose, onEdit, onDelete }) => {
  if (!node) return null;

  const nodeType = NODE_TYPES[node.data.type] || NODE_TYPES.source;
  const riskLevel = RISK_LEVELS[node.data.risk] || RISK_LEVELS.low;
  const Icon = nodeType.icon;
  const RiskIcon = riskLevel.icon;

  return (
    <div 
      data-testid="node-details-sidebar"
      className="w-[400px] h-full border-l border-slate-800 bg-slate-950/95 backdrop-blur-xl overflow-y-auto"
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur-xl border-b border-slate-800 p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-12 h-12 rounded-lg flex items-center justify-center",
              "bg-slate-800"
            )}>
              <Icon className={cn("h-6 w-6", nodeType.color)} />
            </div>
            <div>
              <Badge variant="outline" className="mb-1 text-xs">
                {nodeType.label}
              </Badge>
              <h2 className="text-lg font-bold text-white">{node.data.label}</h2>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Risk Assessment */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-300">Risk Assessment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiskIcon className={cn("h-5 w-5", riskLevel.color.split(' ')[0])} />
                <span className={cn("font-semibold", riskLevel.color.split(' ')[0])}>
                  {riskLevel.label}
                </span>
              </div>
              {node.data.riskScore && (
                <Badge variant="outline" className={cn("text-sm", riskLevel.color)}>
                  Score: {node.data.riskScore}/100
                </Badge>
              )}
            </div>
            {node.data.riskFactors && (
              <div className="mt-3 pt-3 border-t border-slate-800">
                <p className="text-xs text-slate-500 mb-2">Risk Factors:</p>
                <div className="flex flex-wrap gap-1">
                  {node.data.riskFactors.map((factor, i) => (
                    <Badge key={i} variant="outline" className="text-xs bg-slate-800/50">
                      {factor}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Description */}
        {node.data.description && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-400">{node.data.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Data Categories */}
        {node.data.dataCategories && node.data.dataCategories.length > 0 && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300">Data Categories</CardTitle>
              <CardDescription className="text-xs">Personal data types processed</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {node.data.dataCategories.map((category, i) => (
                  <Badge key={i} variant="outline" className="bg-violet-500/10 text-violet-400 border-violet-500/20">
                    {category}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Legal Basis */}
        {node.data.legalBasis && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300">Legal Basis</CardTitle>
            </CardHeader>
            <CardContent>
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                {node.data.legalBasis}
              </Badge>
              {node.data.purpose && (
                <p className="text-sm text-slate-400 mt-2">{node.data.purpose}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Security Measures */}
        {node.data.securityMeasures && node.data.securityMeasures.length > 0 && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Security Measures
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {node.data.securityMeasures.map((measure, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm text-slate-300">{measure}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cross-Border Transfer Details */}
        {node.data.type === 'transfer' && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Transfer Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {node.data.country && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Destination</span>
                  <span className="text-sm text-white font-medium">{node.data.country}</span>
                </div>
              )}
              {node.data.hasAdequacy !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Adequacy Decision</span>
                  <Badge variant="outline" className={node.data.hasAdequacy 
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                  }>
                    {node.data.hasAdequacy ? 'Yes' : 'No'}
                  </Badge>
                </div>
              )}
              {node.data.safeguards && (
                <div>
                  <span className="text-sm text-slate-400 block mb-1">Safeguards</span>
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                    {node.data.safeguards}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Retention */}
        {node.data.retention && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300">Retention Period</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-white font-medium">{node.data.retention}</p>
            </CardContent>
          </Card>
        )}

        {/* Related ROPA */}
        {node.data.ropaId && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Related ROPA Record
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" className="w-full justify-between" size="sm">
                <span>View ROPA Record</span>
                <ExternalLink className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t border-slate-800">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => onEdit && onEdit(node)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Node
          </Button>
          <Button 
            variant="outline" 
            className="text-rose-400 hover:text-rose-300 hover:border-rose-500/50"
            onClick={() => onDelete && onDelete(node)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NodeDetailsSidebar;
